import { MapUtils } from '../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
  cloneDataOrNull, isSameData,
} from '../dto/dto.js';
import { ApplicationFeeProtocol } from './application-fee/application-fee-protocol.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { VersionRange } from '../core/version-range.js';

export class GiftCardSellingSettings extends DataModel {
  static readonly TYPE_ID: string = '6a06aca704ff4d3e9af7534911961752';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly paymentAccount: PaymentAccountRef | null,
    public readonly feeProtocol: ApplicationFeeProtocol | null,
    public readonly allowLegalEntityInvoices: boolean,
    public readonly allowOfflineOrderReceipts: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return GiftCardSellingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GiftCardSellingSettings.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    paymentAccount: PaymentAccountRef | null,
    feeProtocol: ApplicationFeeProtocol | null,
    allowLegalEntityInvoices: boolean,
    allowOfflineOrderReceipts: boolean,
  }): GiftCardSellingSettings {
    return new GiftCardSellingSettings(
        args.businessRef,
        args.paymentAccount,
        args.feeProtocol,
        args.allowLegalEntityInvoices,
        args.allowOfflineOrderReceipts,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardSellingSettings {
    return GiftCardSellingSettings.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      paymentAccount: context.fromDataOrNull<PaymentAccountRef>(MapUtils.getRecordOrNull(map, 'paymentAccount')),
      feeProtocol: context.fromDataOrNull<ApplicationFeeProtocol>(MapUtils.getRecordOrNull(map, 'feeProtocol')),
      allowLegalEntityInvoices: MapUtils.getBoolean(map, 'allowLegalEntityInvoices'),
      allowOfflineOrderReceipts: MapUtils.getBoolean(map, 'allowOfflineOrderReceipts'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'paymentAccount': context.toDataOrNull(this.paymentAccount),
      'feeProtocol': context.toDataOrNull(this.feeProtocol),
      'allowLegalEntityInvoices': this.allowLegalEntityInvoices,
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessRef = this.businessRef.clone(),
    paymentAccount = cloneDataOrNull(this.paymentAccount),
    feeProtocol = cloneDataOrNull(this.feeProtocol),
    allowLegalEntityInvoices = this.allowLegalEntityInvoices,
    allowOfflineOrderReceipts = this.allowOfflineOrderReceipts,
  }: {
    businessRef?: BusinessModuleRef,
    paymentAccount?: PaymentAccountRef | null,
    feeProtocol?: ApplicationFeeProtocol | null,
    allowLegalEntityInvoices?: boolean,
    allowOfflineOrderReceipts?: boolean,
  } = {}): this {
    return GiftCardSellingSettings.create({
      businessRef,
      paymentAccount,
      feeProtocol,
      allowLegalEntityInvoices,
      allowOfflineOrderReceipts,
    }) as this;
  }

  isSame(other: GiftCardSellingSettings): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.paymentAccount, this.paymentAccount) &&
      isSameData(other.feeProtocol, this.feeProtocol) &&
      other.allowLegalEntityInvoices === this.allowLegalEntityInvoices &&
      other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts;
  }
}

export class GiftCardSellingSettingsBuilder extends DataModelBuilder<GiftCardSellingSettings> {
  build(context: SerializationContext, map: SerializedData): GiftCardSellingSettings {
    return GiftCardSellingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(GiftCardSellingSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GiftCardSellingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
